import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Export data to PDF format
 */
export const exportToPDF = (data, columns, title = 'Report') => {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  doc.text(title, 14, 22);
  
  // Add date
  doc.setFontSize(10);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);
  
  // Prepare table data
  const tableColumn = columns.map(col => col.header);
  const tableRows = data.map(row => 
    columns.map(col => {
      if (col.render) {
        // For rendered columns, try to extract text
        return typeof col.render(row) === 'string' ? col.render(row) : row[col.accessor] || '';
      }
      return row[col.accessor] || '';
    })
  );
  
  // Add table
  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 35,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [14, 165, 233] },
  });
  
  // Save PDF
  doc.save(`${title.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
};

/**
 * Export data to Excel format
 */
export const exportToExcel = (data, columns, filename = 'Report') => {
  // Prepare data for Excel
  const excelData = data.map(row => {
    const rowData = {};
    columns.forEach(col => {
      if (col.render) {
        rowData[col.header] = typeof col.render(row) === 'string' ? col.render(row) : row[col.accessor] || '';
      } else {
        rowData[col.header] = row[col.accessor] || '';
      }
    });
    return rowData;
  });
  
  // Create worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  
  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  
  // Save file
  XLSX.writeFile(wb, `${filename.replace(/\s+/g, '_')}_${Date.now()}.xlsx`);
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (data, columns, filename = 'Report') => {
  // Prepare CSV content
  const headers = columns.map(col => col.header).join(',');
  const rows = data.map(row => 
    columns.map(col => {
      let value;
      if (col.render) {
        value = typeof col.render(row) === 'string' ? col.render(row) : row[col.accessor] || '';
      } else {
        value = row[col.accessor] || '';
      }
      // Escape commas and quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',')
  ).join('\n');
  
  const csv = `${headers}\n${rows}`;
  
  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename.replace(/\s+/g, '_')}_${Date.now()}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export analytics report to PDF
 */
export const exportAnalyticsPDF = (analyticsData, collegeName) => {
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(20);
  doc.text('College Analytics Report', 14, 22);
  
  // College name
  doc.setFontSize(14);
  doc.text(collegeName, 14, 32);
  
  // Date
  doc.setFontSize(10);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 40);
  
  // Summary Section
  doc.setFontSize(12);
  doc.text('Summary Statistics', 14, 52);
  
  const summaryData = [
    ['Total Students', analyticsData.students?.total || 0],
    ['Active Students', analyticsData.students?.active || 0],
    ['Total Courses', analyticsData.courses?.total || 0],
  ];
  
  doc.autoTable({
    body: summaryData,
    startY: 58,
    theme: 'grid',
    headStyles: { fillColor: [14, 165, 233] },
  });
  
  // KPIs Section
  let finalY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(12);
  doc.text('Key Performance Indicators', 14, finalY);
  
  const kpiData = [
    ['Course Completion Rate', `${analyticsData.kpis?.courseCompletion?.toFixed(1) || 0}%`],
    ['Wellbeing Index', `${analyticsData.kpis?.wellbeingIndex || 0}`],
    ['Cognitive Index', `${analyticsData.kpis?.cognitiveIndex || 0}`],
  ];
  
  doc.autoTable({
    body: kpiData,
    startY: finalY + 6,
    theme: 'grid',
    headStyles: { fillColor: [14, 165, 233] },
  });
  
  // Save
  doc.save(`${collegeName.replace(/\s+/g, '_')}_Analytics_${Date.now()}.pdf`);
};

/**
 * Format date for export
 */
export const formatDateForExport = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format currency for export
 */
export const formatCurrencyForExport = (amount) => {
  if (!amount) return '₹0';
  return `₹${amount.toLocaleString('en-IN')}`;
};
